/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DealerApiCommunications } from '../../webmodule-common/other/api/dealer-api-communications';
import { displayFrameConfigLoadErrorMsg, GlobalSupplierNotification } from './supplier-services';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { LangSupplierServices } from '../../localization/scripts/v6config/supplier-services-lang';
import { responseHandler } from '../api/api-response-handler';
import { sharedExecute } from '../../webmodule-common/other/common/helpers/callbacks';

import { ApiCommunications, V6OfflineCause } from '../../webmodule-common/interop/webmodule-interop';
import { LangV6Config } from '../../localization/scripts/v6config/v6config-lang';
import {
  v6bootloaderStale,
  v6FrameConfigScriptLoader,
  V6LoaderFailureReason
} from '../../webmodule-common/interop/interfaces/v6config-loader';
// Re-export v6bootloaderStale for backward compatibility
export { v6bootloaderStale };

let _v6commSingleton: ApiCommunications | undefined;
export const v6ApiInjecterEvent = () => {
  if (!_v6commSingleton)
    _v6commSingleton = new DealerApiCommunications(globalThis.dealerConfiguration.v6apiHost, responseHandler, () => {
      //Redirect to home page, next query will force a login to occur
      window.location.href = '/login';
    });
  return _v6commSingleton;
};

// eslint-disable-next-line no-var -- var declared as it is replaced by
declare var __localdebug__: string;

// Track the last failure to avoid showing the same error message repeatedly
let _lastFailureReason: V6LoaderFailureReason | undefined;
let _lastFailureMessage: string | undefined;

export const v6BootLoader = sharedExecute(() => v6BootLoaderInternal(false));
export const v6BootLoaderWithFailMsg = sharedExecute(() => v6BootLoaderInternal(true));

async function v6BootLoaderInternal(alwaysShowFailMsg: boolean): Promise<boolean> {
  const comms = v6ApiInjecterEvent();

  // Call the core loader (handles stale checks and version mismatches internally)
  const result = await v6FrameConfigScriptLoader(comms);

  if (result.success) {
    // Reset failure tracking on success
    _lastFailureReason = undefined;
    _lastFailureMessage = undefined;

    // findInstance, not getInstance: this is only a lookup against whatever the normal
    // setState-driven supplier refresh has already registered - it must not create a phantom
    // GlobalSupplierNotification for an id that isn't otherwise tracked (round 4 review).
    const gsn = result.supplierId ? GlobalSupplierNotification.findInstance(result.supplierId) : undefined;

    // @SUPOFF-BOOT-002 (amended): shows the one-off "supplier is offline" notice exactly once per
    // offline episode when booting from a cached (or server-last-good) version - notifyOfflineOnce
    // shares its "already shown" flag with makeOffline()'s own mid-session drop path, so a dealer
    // who already got the mid-session modal doesn't get it again here, and vice versa (round 3
    // review). The flag is cleared by GlobalSupplierNotification.setState's own transition to
    // online (round 4 review) - not by this function - since a transient undefined, a
    // sharedExecute join, or a mid-outage live result could all reach `result.success` without
    // ever being the specific "reset" case this function used to look for.
    //
    // showOfflineNotice is false when the "offline" signal was really just our own version check
    // failing to reach the server at all (dealerVersion undefined - typically WebModule itself
    // restarting) - the supplier's state hasn't changed, so no notice fires for that, even though
    // the boot itself still proceeds on the cached version.
    if (result.bootedFromCache && result.showOfflineNotice && gsn) {
      // The reason shown is the cause category (Connection/Upgrading) via the same dialog a
      // mid-session drop shows - the free-text offlineMessage is not surfaced to dealers.
      await gsn.notifyOfflineOnce(result.offlineCause ?? V6OfflineCause.Connection, result.message);
    }

    return true;
  }

  // Determine if we should show the error message:
  // - alwaysShowFailMsg (withFailMsg): ALWAYS show the message
  // - otherwise: only show if this is a DIFFERENT failure from the last one
  const isDifferentFailure = _lastFailureReason !== result.failureReason || _lastFailureMessage !== result.message;
  const shouldShowMessage = alwaysShowFailMsg || isDifferentFailure;

  // Update last failure tracking
  _lastFailureReason = result.failureReason;
  _lastFailureMessage = result.message;

  if (shouldShowMessage) {
    switch (result.failureReason) {
      case V6LoaderFailureReason.SupplierOffline:
        await GlobalSupplierNotification.showOfflineMessage(
          result.offlineCause ?? V6OfflineCause.Connection,
          result.message
        );
        break;

      // @SUPOFF-BOOT-006: the supplier is online here, reporting a config it cannot use - a fault
      // on its side, not an outage. Must show the frame-config-could-not-load error, never the
      // "Supplier OFFLINE" title/text that case above uses.
      case V6LoaderFailureReason.InvalidVersion:
      case V6LoaderFailureReason.ScriptLoadFailed:
      case V6LoaderFailureReason.ConfigInitFailed:
        await displayFrameConfigLoadErrorMsg();
        break;

      case V6LoaderFailureReason.UnsupportedVersion:
        // Shares the same neutral title as the frame-config faults above - this is a fault too,
        // not the supplier being offline.
        await information(LangV6Config.v6OptimizedBlobStorageInfo(), LangSupplierServices.frameConfigurationTitle());
        break;
    }
  }

  return false;
}
